import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CircularProgress, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SwalCustomClass } from '../../components/general/toastSwal/swalCustomClass';
import FormView from '../../components/views/formView';
import FieldText from '../../components/general/fields/fieldText';
import {
    asyncGetLatestVoiceBlaster,
    asyncPostVoiceBlaster,
} from '../../../services/voiceBlaster.service';
import { HOME } from '../../../routes/paths';
import { MasterMessage } from '../../../masters/masterMessages';
import { IconWrapper } from '../auth/styled';
import SaveIcon from '@mui/icons-material/Save';

type Props = {
    onSave?: () => void;
};

const VoiceBlasterForm = ({ onSave }: Props) => {
    const navigate = useNavigate();
    const { voiceBlaster }: any = MasterMessage;
    const [loadingForm, setLoadingForm] = useState(false);
    const [loadingInitial, setLoadingInitial] = useState(true);

    const formik = useFormik({
        initialValues: {
            text: '',
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            text: Yup.string()
                .required('El mensaje es obligatorio')
                .max(200, 'El mensaje no puede superar los 200 caracteres'),
        }),
        onSubmit: async (formValues) => {
            try {
                setLoadingForm(true);
                const body: any = await asyncPostVoiceBlaster(formValues);
                if (body?.items?.id) {
                    if (onSave) onSave();
                    SwalCustomClass.fire({
                        text: voiceBlaster.messageEdit,
                        icon: 'success',
                        iconColor: '#1d1d1d',
                    });
                    navigate(HOME);
                } else {
                    SwalCustomClass.fire({
                        text: voiceBlaster.messageError,
                        icon: 'error',
                        iconColor: '#1d1d1d',
                    });
                }
            } catch (error: any) {
                SwalCustomClass.fire({
                    text: error?.message || voiceBlaster.messageError,
                    icon: 'error',
                    iconColor: '#1d1d1d',
                });
            } finally {
                setLoadingForm(false);
            }
        },
    });

    useEffect(() => {
        const loadLatestMessage = async () => {
            try {
                setLoadingInitial(true);
                const lastMessage = await asyncGetLatestVoiceBlaster();
                if (lastMessage?.text) {
                    formik.setValues({ text: lastMessage.text });
                }
            } catch (error) {
                SwalCustomClass.fire({
                    text: voiceBlaster.messageError,
                    icon: 'error',
                    iconColor: '#1d1d1d',
                });
            } finally {
                setLoadingInitial(false);
            }
        };

        loadLatestMessage();
    }, []);

    if (loadingInitial) {
        return (
            <IconWrapper>
                <CircularProgress size={70} thickness={2} />
            </IconWrapper>
        );
    }

    return (
        <>
            <FormView
                title={voiceBlaster.titleEdit}
                onClickButtonLoading={formik.handleSubmit}
                onClickBack={() => navigate(HOME)}
                enabledAddLoading={true}
                loadingButtonLoading={loadingForm}
                startIconButtonLoading={<SaveIcon />}
                titleButtonLoading="Guardar"
                disableAdd={true}
            />
            <form className="form">
                <Grid container spacing={{ xs: 1.5, md: 1.5 }}>
                    <Grid item xs={12}>
                        <FieldText
                            label="Mensaje"
                            name="text"
                            size="small"
                            required
                            touched={formik.touched.text}
                            errors={formik.errors.text}
                            value={formik.values.text}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                </Grid>
            </form>
        </>
    );
};

export default VoiceBlasterForm;
